import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormikProps } from "formik";
import { isEmpty } from "lodash";
import routes from "routes/routes";
import { useAppSelector } from "store/hooks";
import {
  companiesListSelector,
  companyMetadataSelector,
  userMetadataSelector,
} from "store/selectors";

import { EMagicLinkFlow } from "types/AuthFlowTypes";
import { showErrorModal } from "helpers/index";
import { getIsSetupCompletedWithoutPassword } from "helpers/shared/guardRedirect";
import useAuth from "hooks/useAuth";
import { PasswordFormSchemasType } from "components/auth";

import {
  mutationUsersControllerAcceptTermsAndConditions,
  mutationUsersControllerCreateOrUpdatePassword,
  UserProfileResponseDto,
} from "utils/swagger_react_query";

import { IAuthInvitationSearchParams, useCompleteSignIn } from "../_shared";
import { EmployeeSignUpSteps } from "./types";

export const useEmployeeSignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useAppSelector(userMetadataSelector);
  const currentCompany = useAppSelector(companyMetadataSelector);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [svoc, setSvoc] = useState<boolean>(false);
  const currentCompaniesList = useAppSelector(companiesListSelector);

  const [currentStep, setCurrentStep] = useState<EmployeeSignUpSteps>(
    EmployeeSignUpSteps.ACCEPT_INVITATION,
  );
  const { isAuthenticated, getCurrentUser, getCurrentUserAndCompany } = useAuth();
  const { actions, metadata } = useCompleteSignIn({
    mode: EMagicLinkFlow.SIGN_UP,
    autoLogin: false,
  });

  const isSetupCompletedWithoutPassword = (user?: UserProfileResponseDto | null | undefined) =>
    getIsSetupCompletedWithoutPassword(user ?? currentUser);

  useEffect(() => {
    autoLogin(EMagicLinkFlow.SIGN_UP, getAuthParams());
  }, []);

  const getAuthParams = (): IAuthInvitationSearchParams => {
    return {
      invitationId: searchParams.get("invitationId") || undefined,
      error: searchParams.get("error") || undefined,
    };
  };

  const determineInitialStep = (user: UserProfileResponseDto | null | undefined) => {
    if (isSetupCompletedWithoutPassword(user)) {
      setCurrentStep(EmployeeSignUpSteps.SET_PASSWORD);
    } else {
      setCurrentStep(EmployeeSignUpSteps.ACCEPT_INVITATION);
    }
  };

  const autoLogin = async (mode: EMagicLinkFlow, authParams: IAuthInvitationSearchParams) => {
    if (searchParams.size || (searchParams.size && isAuthenticated())) {
      const userWithCompanyRes = await actions.completeSignIn(
        mode,
        { error: authParams.error, code: authParams.invitationId },
        authParams.invitationId,
      );
      if (!isEmpty(userWithCompanyRes?.user)) {
        determineInitialStep(userWithCompanyRes.user);
      }
    } else if (!isAuthenticated()) {
      navigate(routes.SIGN_IN);
    } else {
      const userDetailsRes = await actions.checkUserData();
      determineInitialStep(userDetailsRes?.user || null);
    }

    setLoading(false);
    actions.setLoading(false);
  };

  const handleSetPassword = async (
    formikProps: FormikProps<PasswordFormSchemasType["CREATE_PASSWORD"]>,
  ) => {
    setSvoc(true);
    const { validateForm, values } = formikProps;
    const errors = await validateForm(values);
    if (!isEmpty(errors)) return;
    const companiesIds = currentCompaniesList?.companies?.map((company) => company.companyId);

    try {
      setLoading(true);
      await mutationUsersControllerCreateOrUpdatePassword()({
        newPassword: values.newPassword,
      });
      const userDetails = await getCurrentUserAndCompany();
      if (userDetails?.user) {
        actions.redirectByRoleAndSignUpStatus(userDetails, companiesIds || []);
      }
    } catch (error) {
      showErrorModal(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    try {
      setLoading(true);
      await mutationUsersControllerAcceptTermsAndConditions()();
      const userRes = await getCurrentUser();
      if (!userRes?.isPasswordSet) {
        setCurrentStep(EmployeeSignUpSteps.SET_PASSWORD);
        return;
      }
      const companiesIds = currentCompaniesList?.companies?.map((company) => company.companyId);
      actions.redirectByRoleAndSignUpStatus(
        { user: userRes, company: currentCompany },
        companiesIds || [],
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    metadata: {
      isLoading,
      currentUser,
      pageLoader: metadata.isLoading,
      currentStep,
      isSetupCompletedWithoutPassword,
      svoc,
    },
    actions: {
      handleAcceptInvitation,
      handleSetPassword,
    },
  };
};
